import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert } from '@/components/icons';
import { useTranslation } from 'react-i18next';
import { useProperty } from '@/contexts/property-context';
import { EmptyState } from '@/components/common/EmptyState';

interface RequireStaffProps {
  children: React.ReactNode;
}

/**
 * Blocks management-only screens for accounts without a staff hat.
 *
 * The Shell already hides these nav links for residents, but a URL can always
 * be typed. The backend enforces real access control independently
 * (`PropertyService.get_by_id_for_user`), so this is a UX guard: it turns a
 * failed API call into a proper "not available" state instead of a blank or
 * broken page.
 */
export const RequireStaff: React.FC<RequireStaffProps> = ({ children }) => {
  const { hasStaffHat } = useProperty();
  const navigate = useNavigate();
  const { t } = useTranslation();

  if (!hasStaffHat) {
    return (
      <EmptyState
        icon={ShieldAlert}
        title={t('access.staff_only_title', 'This area is for property managers')}
        description={t(
          'access.staff_only_desc',
          'Your account does not manage any property, so this section is not available. If you think this is wrong, contact your property manager.'
        )}
        actionLabel={t('common.back_to_overview', 'Back to Overview')}
        onAction={() => navigate('/app')}
      />
    );
  }

  return <>{children}</>;
};